import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ExternalLink,
  FileText,
  ImageIcon,
  Link2,
  Loader2,
  Music,
  Play,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useLeadConversationAttachments } from "@/hooks/useLeadConversationAttachments";
import type {
  AttachmentItem,
  AttachmentMonthGroup,
  DocAttachmentItem,
  LinkAttachmentItem,
  MediaAttachmentItem,
} from "@/lib/conversation-attachments";

interface LeadMediaLinksDocsProps {
  leadId: string;
  /** "collapsible" = dropdown na aba Informações; "panel" = conteúdo direto na aba Mídias */
  variant?: "collapsible" | "panel";
}

function MediaTypeIcon({ type }: { type: string }) {
  if (type === "video") return <Video className="h-4 w-4 text-white" />;
  if (type === "audio") return <Music className="h-4 w-4 text-white" />;
  return <ImageIcon className="h-4 w-4 text-white" />;
}

function MonthSections<T extends AttachmentItem>({
  groups,
  emptyLabel,
  renderItem,
  countSuffix,
}: {
  groups: AttachmentMonthGroup<T>[];
  emptyLabel: string;
  renderItem: (item: T) => ReactNode;
  countSuffix: (n: number) => string;
}) {
  if (groups.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.label} className="space-y-2">
          <p className="text-sm font-medium text-foreground">{group.label}</p>
          <div className="grid grid-cols-3 gap-1.5">{group.items.map(renderItem)}</div>
          <p className="text-right text-xs text-muted-foreground">
            {countSuffix(group.items.length)}
          </p>
        </div>
      ))}
    </div>
  );
}

function MediaTile({ item }: { item: MediaAttachmentItem }) {
  const [failed, setFailed] = useState(false);
  const showThumb =
    !failed && item.thumbnailUrl && (item.mediaType === "image" || item.mediaType === "video");

  return (
    <button
      type="button"
      className="group relative aspect-square w-full overflow-hidden rounded-md bg-muted"
      onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
      title={item.label}
    >
      {showThumb ? (
        <img
          src={item.thumbnailUrl}
          alt={item.label}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-primary/15">
          <MediaTypeIcon type={item.mediaType} />
        </div>
      )}
      {item.mediaType === "video" && showThumb && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Play className="h-6 w-6 fill-white text-white" />
        </span>
      )}
      <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1 py-0.5 text-[9px] text-white opacity-0 transition-opacity group-hover:opacity-100">
        Abrir
      </span>
    </button>
  );
}

function DocTile({ item }: { item: DocAttachmentItem }) {
  const ext = item.fileName.includes(".")
    ? item.fileName.split(".").pop()?.toUpperCase()
    : "DOC";

  return (
    <button
      type="button"
      className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/40 p-2 text-center hover:bg-muted"
      onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
      title={item.fileName}
    >
      <FileText className="h-6 w-6 text-primary shrink-0" />
      <span className="line-clamp-2 w-full text-[10px] font-medium leading-tight">
        {item.fileName}
      </span>
      <span className="text-[9px] text-muted-foreground">{ext}</span>
    </button>
  );
}

function LinkTile({ item }: { item: LinkAttachmentItem }) {
  return (
    <button
      type="button"
      className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/30 p-2 text-center hover:bg-muted"
      onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
      title={item.url}
    >
      <Link2 className="h-5 w-5 shrink-0 text-primary" />
      <span className="line-clamp-2 w-full text-[10px] font-medium leading-tight">
        {item.domain}
      </span>
      {item.preview && item.preview !== item.domain && (
        <span className="line-clamp-1 w-full text-[9px] text-muted-foreground">
          {item.preview}
        </span>
      )}
    </button>
  );
}

function LeadMediaLinksDocsContent({
  leadId,
  counts,
  loading,
  error,
  hasConversations,
  mediaGroups,
  linkGroups,
  docGroups,
  scrollMaxHeight = "280px",
}: {
  leadId: string;
  counts: { media: number; links: number; docs: number; total: number };
  loading: boolean;
  error: string | null;
  hasConversations: boolean;
  mediaGroups: AttachmentMonthGroup<MediaAttachmentItem>[];
  linkGroups: AttachmentMonthGroup<LinkAttachmentItem>[];
  docGroups: AttachmentMonthGroup<DocAttachmentItem>[];
  scrollMaxHeight?: string;
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando…
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-center text-sm text-destructive">{error}</p>;
  }

  if (!hasConversations) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        Nenhuma conversa ainda. Inicie o atendimento para ver mídia, links e documentos.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <Tabs defaultValue="media" className="w-full">
        <TabsList className="grid h-9 w-full grid-cols-3 p-0.5">
          <TabsTrigger value="media" className="text-xs px-1">
            Mídia{counts.media > 0 ? ` (${counts.media})` : ""}
          </TabsTrigger>
          <TabsTrigger value="links" className="text-xs px-1">
            Links{counts.links > 0 ? ` (${counts.links})` : ""}
          </TabsTrigger>
          <TabsTrigger value="docs" className="text-xs px-1">
            Docs{counts.docs > 0 ? ` (${counts.docs})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="media"
          className={cn("mt-3 overflow-y-auto", scrollMaxHeight !== "none" && "max-h-[280px]")}
        >
          <MonthSections
            groups={mediaGroups}
            emptyLabel="Nenhuma mídia nesta conversa."
            countSuffix={(n) => `${n} ${n === 1 ? "item" : "itens"}`}
            renderItem={(item) => <MediaTile key={item.id} item={item} />}
          />
        </TabsContent>

        <TabsContent
          value="links"
          className={cn("mt-3 overflow-y-auto", scrollMaxHeight !== "none" && "max-h-[280px]")}
        >
          <MonthSections
            groups={linkGroups}
            emptyLabel="Nenhum link nesta conversa."
            countSuffix={(n) => `${n} ${n === 1 ? "link" : "links"}`}
            renderItem={(item) => <LinkTile key={item.id} item={item} />}
          />
        </TabsContent>

        <TabsContent
          value="docs"
          className={cn("mt-3 overflow-y-auto", scrollMaxHeight !== "none" && "max-h-[280px]")}
        >
          <MonthSections
            groups={docGroups}
            emptyLabel="Nenhum documento nesta conversa."
            countSuffix={(n) => `${n} ${n === 1 ? "documento" : "documentos"}`}
            renderItem={(item) => <DocTile key={item.id} item={item} />}
          />
        </TabsContent>
      </Tabs>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => navigate(`/conversations?lead_id=${leadId}`)}
      >
        <ExternalLink className="mr-2 h-3.5 w-3.5" />
        Ver na conversa
      </Button>
    </div>
  );
}

export function LeadMediaLinksDocs({ leadId, variant = "collapsible" }: LeadMediaLinksDocsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isPanel = variant === "panel";
  const {
    mediaGroups,
    linkGroups,
    docGroups,
    counts,
    loading,
    hasConversations,
    error,
  } = useLeadConversationAttachments(leadId, isPanel || isOpen);

  if (isPanel) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Mídia, links e docs</h3>
          {counts.total > 0 && (
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
              {counts.total}
            </Badge>
          )}
        </div>
        <LeadMediaLinksDocsContent
          leadId={leadId}
          counts={counts}
          loading={loading}
          error={error}
          hasConversations={hasConversations}
          mediaGroups={mediaGroups}
          linkGroups={linkGroups}
          docGroups={docGroups}
          scrollMaxHeight="none"
        />
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-0 text-foreground hover:bg-muted/50 hover:text-foreground"
        >
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Mídia, links e docs</h3>
            {counts.total > 0 && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {counts.total}
              </Badge>
            )}
          </div>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="pt-2">
        <LeadMediaLinksDocsContent
          leadId={leadId}
          counts={counts}
          loading={loading}
          error={error}
          hasConversations={hasConversations}
          mediaGroups={mediaGroups}
          linkGroups={linkGroups}
          docGroups={docGroups}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
